import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, Share2, Flag, Send, Eye, UserPlus, UserCheck, PictureInPicture2, Maximize2, Lock, Cast, Tv, Gift, Plus, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { initCast, castMedia, stopCast, onCastChange, castState, isCastSupported } from "@/lib/cast";
import { saveOffline, getOffline, removeOffline } from "@/lib/offline";

const TIP_PRESETS = [50, 100, 250, 500];

export default function Watch() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [v, setV] = useState(null);
  const [comments, setComments] = useState([]);
  const [liked, setLiked] = useState(false);
  const [text, setText] = useState("");
  const [following, setFollowing] = useState(false);
  const videoRef = useRef(null);
  const [speed, setSpeed] = useState(1);
  const [resumed, setResumed] = useState(false);
  const [tvMode, setTvMode] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [tipCustom, setTipCustom] = useState("");
  const [showCast, setShowCast] = useState(false);
  const [castInfo, setCastInfo] = useState({ ready: false, connected: false });
  const [playlists, setPlaylists] = useState([]);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dlPct, setDlPct] = useState(null);

  useEffect(() => { getOffline(id).then(r => setSaved(!!r)).catch(() => {}); }, [id]);

  const [showDl, setShowDl] = useState(false);
  const [sdPrep, setSdPrep] = useState(false);

  const toggleDownload = async () => {
    if (saved) { await removeOffline(id); setSaved(false); toast.success("Removed from downloads"); return; }
    setShowDl(true);
  };
  const startDownload = async (quality) => {
    let url = v.video_url;
    if (quality === "sd") {
      setSdPrep(true);
      try {
        let { data } = await api.post(`/videos/${id}/sd`);
        for (let i = 0; data.status === "processing" && i < 200; i++) {
          await new Promise(r => setTimeout(r, 3000));
          ({ data } = await api.get(`/videos/${id}/sd`));
        }
        if (data.status !== "ready") throw new Error("Couldn't prepare the data saver version. Try Full quality.");
        url = data.url;
      } catch (e) { toast.error(apiErr(e)); setSdPrep(false); return; }
      setSdPrep(false);
    }
    setShowDl(false); setDlPct(0);
    try { await saveOffline(v, setDlPct, quality, url); setSaved(true); toast.success("Saved for offline viewing"); }
    catch (e) { toast.error(e.message || "Download failed"); }
    finally { setDlPct(null); }
  };

  useEffect(() => { initCast(); const off = onCastChange(setCastInfo); setCastInfo(castState()); return off; }, []);

  useEffect(() => {
    api.get(`/videos/${id}`).then(r=>setV(r.data)).catch(()=>toast.error("Video not found"));
    api.get(`/videos/${id}/comments`).then(r=>setComments(r.data));
  }, [id]);

  useEffect(() => {
    if (!user || !v) return;
    api.get(`/videos/${id}/liked`).then(r=>setLiked(r.data.liked));
    api.get("/watch-history").then(({data}) => {
      const h = data.find(x => x.video_id === id);
      if (h && !resumed && videoRef.current) { videoRef.current.currentTime = h.position; setResumed(true); }
    });
  }, [user, v, id, resumed]);

  useEffect(() => {
    if (!v || !user) return;
    const iv = setInterval(() => {
      if (videoRef.current && !videoRef.current.paused) {
        api.post(`/videos/${id}/progress`, { position: videoRef.current.currentTime }).catch(()=>{});
      }
    }, 8000);
    return () => clearInterval(iv);
  }, [v, user, id]);

  const like = async () => {
    if (!user) return nav("/login");
    try { const {data} = await api.post(`/videos/${id}/like`); setLiked(data.liked); setV(x=>({...x, likes: x.likes+(data.liked?1:-1)})); } catch(e){toast.error(apiErr(e));}
  };
  const share = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); } catch { toast.error("Copy failed"); }
  };
  const comment = async () => {
    if (!user) return nav("/login");
    if (!text.trim()) return;
    try { const {data} = await api.post(`/videos/${id}/comments`, { text }); setComments(c=>[data, ...c]); setText(""); }
    catch(e){toast.error(apiErr(e));}
  };
  const follow = async () => {
    if (!user) return nav("/login");
    try { const {data} = await api.post(`/users/${v.creator.id}/follow`); setFollowing(data.following); }
    catch(e){toast.error(apiErr(e));}
  };
  const report = async () => {
    if (!user) return nav("/login");
    const reason = prompt("Report reason (copyright, harassment, hate, sexual, violence, scam, spam, illegal, other):"); if (!reason) return;
    try { await api.post("/reports", { target_type: "video", target_id: id, reason }); toast.success("Report submitted"); }
    catch(e){toast.error(apiErr(e));}
  };
  const pip = async () => { try { await videoRef.current.requestPictureInPicture(); } catch { toast.error("PiP not supported"); } };
  const full = async () => { try { await videoRef.current.requestFullscreen(); } catch {} };
  const changeSpeed = (s) => { setSpeed(s); if (videoRef.current) videoRef.current.playbackRate = s; };

  const unlock = async () => {
    if (!user) return nav("/login");
    try { await api.post(`/videos/${id}/purchase`); toast.success("Unlocked! Enjoy."); refresh(); const {data} = await api.get(`/videos/${id}`); setV(data); }
    catch(e){toast.error(apiErr(e));}
  };

  const sendTip = async (coins) => {
    if (!user) return nav("/login");
    try { await api.post(`/videos/${id}/tip`, { coins }); toast.success(`Tipped ${coins} coins!`); setShowTip(false); refresh(); }
    catch(e){toast.error(apiErr(e));}
  };

  const openPlaylistPicker = async () => {
    if (!user) return nav("/login");
    const { data } = await api.get(`/playlists?user_id=${user.id}`);
    setPlaylists(data); setShowPlaylist(true);
  };
  const addToPlaylist = async (pid) => {
    try { await api.post(`/playlists/${pid}/add`, { video_id: id }); toast.success("Added to playlist"); setShowPlaylist(false); }
    catch(e){toast.error(apiErr(e));}
  };

  const castNow = async () => {
    try {
      const url = v.video_url?.startsWith("http") ? v.video_url : (window.location.origin + v.video_url);
      const poster = v.thumbnail_url?.startsWith("http") ? v.thumbnail_url
                    : v.thumbnail_url ? (window.location.origin + v.thumbnail_url) : "";
      await castMedia(url, v.title, `@${v.creator?.username}`, poster);
      toast.success("Casting to " + (castState().deviceName || "TV"));
      setShowCast(false);
      if (videoRef.current) videoRef.current.pause();
    } catch(e) { toast.error(e.message || "Cast failed"); }
  };
  const castStop = async () => { await stopCast(); toast.success("Cast ended"); };

  if (!v) return <div className="p-8 text-white/60">Loading…</div>;

  const locked = v.is_paid && !v.entitled;
  const src = locked ? (v.preview_url || "") : v.video_url;

  return (
    <div className={`mx-auto ${tvMode ? 'max-w-none' : 'max-w-6xl'} px-3 sm:px-6 pt-4`}>
      <div className={`relative rounded-xl overflow-hidden bg-black border border-white/5 ${tvMode ? 'aspect-video max-h-[85vh] mx-auto' : 'aspect-video'}`}>
        {/* Watermark */}
        <div className="pointer-events-none absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md">
          <span className="w-4 h-4 rounded-md bg-[#08D9D6] flex items-center justify-center">
            <svg width="8" height="8" viewBox="0 0 10 10" fill="none"><path d="M2 1L2 9L8 5Z" fill="#000"/></svg>
          </span>
          <span className="text-[10px] font-bold tracking-wide text-white/90">JIXXEO VIDEOS</span>
        </div>
        {locked ? (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 backdrop-blur">
            <div className="w-16 h-16 rounded-full bg-[#FFD700]/20 flex items-center justify-center mb-4"><Lock size={28} className="text-[#FFD700]"/></div>
            <div className="h-font text-2xl font-extrabold text-white">Premium Video</div>
            <div className="text-white/70 mt-1">Unlock for <span className="text-[#FFD700] font-bold">{v.price_coins} coins</span></div>
            <button onClick={unlock} data-testid="unlock-btn"
              className="jx-btn mt-5 h-11 px-6 rounded-full bg-[#FFD700] text-black font-extrabold hover:brightness-110">
              {user ? `Unlock for ${v.price_coins} coins` : "Sign in to unlock"}
            </button>
            <Link to="/coins" className="mt-2 text-xs text-[#08D9D6]">Need more coins? Buy in Coin Shop →</Link>
          </div>
        ) : null}
        {!locked && v.video_url ? (
          <video ref={videoRef} poster={v.thumbnail_url || undefined}
                 controls autoPlay muted playsInline preload="auto"
                 onError={(e)=>{ const t = e.target; console.warn('Video error', t.error); }}
                 className="w-full h-full" data-testid="watch-video">
            <source src={v.video_url} type="video/mp4"/>
            <source src={v.video_url.replace(/\.mp4$/, '.webm')} type="video/webm"/>
            Your browser can't play this video.
          </video>
        ) : locked && v.preview_url ? (
          <video src={v.preview_url} poster={v.thumbnail_url || undefined} muted playsInline autoPlay loop className="w-full h-full opacity-40"/>
        ) : (
          <img src={v.thumbnail_url || undefined} alt={v.title} className="w-full h-full object-cover opacity-40"/>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {[0.5,1,1.5,2].map(s=>(
          <button key={s} onClick={()=>changeSpeed(s)} data-testid={`speed-${s}`}
            className={`chip jx-btn px-3 h-8 rounded-full text-xs font-semibold ${speed===s?'chip-active':''}`}>{s}x</button>
        ))}
        <button onClick={pip} data-testid="pip-btn" className="chip jx-btn px-3 h-8 rounded-full text-xs font-semibold inline-flex items-center gap-1"><PictureInPicture2 size={12}/> PiP</button>
        <button onClick={full} data-testid="full-btn" className="chip jx-btn px-3 h-8 rounded-full text-xs font-semibold inline-flex items-center gap-1"><Maximize2 size={12}/> Fullscreen</button>
        <button onClick={()=>setShowCast(true)} data-testid="cast-btn" className={`chip jx-btn px-3 h-8 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${castInfo.connected ? 'chip-active' : 'text-[#08D9D6]'}`}><Cast size={12}/> {castInfo.connected ? `Casting${castInfo.deviceName?': '+castInfo.deviceName:''}` : 'Cast'}</button>
        <button onClick={()=>setTvMode(!tvMode)} data-testid="tv-mode-btn" className={`chip jx-btn px-3 h-8 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${tvMode?'chip-active':''}`}><Tv size={12}/> TV Mode</button>
        {user && <button onClick={openPlaylistPicker} data-testid="add-playlist-btn" className="chip jx-btn px-3 h-8 rounded-full text-xs font-semibold inline-flex items-center gap-1"><Plus size={12}/> Playlist</button>}
        {!locked && v.video_url && (
          <button onClick={toggleDownload} disabled={dlPct !== null} data-testid="download-btn"
            className={`chip jx-btn px-3 h-8 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${saved ? 'chip-active' : 'text-[#00E7FF]'}`}>
            {saved ? <CheckCircle2 size={12}/> : <Download size={12}/>}
            {dlPct !== null ? `Saving ${dlPct}%` : saved ? 'Downloaded' : 'Download'}
          </button>
        )}
      </div>

      <h1 className="h-font text-2xl sm:text-3xl font-bold mt-4">{v.title}</h1>
      <div className="flex items-center gap-3 text-sm text-white/60 mt-1 flex-wrap">
        <span className="flex items-center gap-1"><Eye size={14}/>{v.views.toLocaleString()} views</span>
        <span>•</span>
        <span>{new Date(v.created_at).toLocaleDateString()}</span>
        <span>•</span>
        <span className="chip px-2 py-0.5 rounded-full text-[11px]">{v.category}</span>
        {v.is_paid && <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40">{v.price_coins} coins</span>}
        {v.hashtags?.map(h=><Link key={h} to={`/search?hashtag=${h}`} className="text-[#08D9D6] hover:underline">#{h}</Link>)}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4 py-3 border-y border-white/5">
        <Link to={`/u/${v.creator?.id}`} className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#08D9D6] to-[#FF2E63] flex items-center justify-center text-black font-bold">
            {(v.creator?.username||'?')[0].toUpperCase()}
          </div>
          <div><div className="font-bold text-sm">@{v.creator?.username}</div></div>
        </Link>
        <button onClick={follow} data-testid="follow-btn" className={`jx-btn ml-2 h-9 px-4 rounded-full text-sm font-bold inline-flex items-center gap-1 ${following?'bg-white/10':'bg-[#08D9D6] text-black hover:bg-[#06B2B0]'}`}>
          {following ? <><UserCheck size={14}/> Following</> : <><UserPlus size={14}/> Follow</>}
        </button>
        <button onClick={()=>setShowTip(true)} data-testid="tip-btn" className="jx-btn h-9 px-4 rounded-full text-sm font-bold inline-flex items-center gap-1 bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/40 hover:bg-[#FFD700]/25">
          <Gift size={14}/> Tip
        </button>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button onClick={like} data-testid="like-btn" className={`jx-btn h-9 px-4 rounded-full text-sm font-semibold inline-flex items-center gap-2 ${liked?'bg-[#FF2E63] text-white':'bg-white/5 hover:bg-white/10'}`}>
            <Heart size={14} className={liked?'fill-white':''}/> {v.likes}
          </button>
          {v.allow_sharing && <button onClick={share} data-testid="share-btn" className="jx-btn h-9 px-4 rounded-full bg-white/5 hover:bg-white/10 text-sm font-semibold inline-flex items-center gap-2"><Share2 size={14}/> Share</button>}
          <Link to={`/copyright?video=${id}`} data-testid="copyright-link" title="Report copyright" className="jx-btn h-9 px-3 rounded-full bg-white/5 hover:bg-white/10 inline-flex items-center gap-1 text-xs font-semibold">©<span className="hidden sm:inline">Copyright</span></Link>
          <button onClick={report} data-testid="report-btn" className="jx-btn h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 inline-flex items-center justify-center"><Flag size={14}/></button>
        </div>
      </div>

      <p className="text-sm text-white/70 mt-4 whitespace-pre-wrap">{v.description}</p>

      {v.allow_comments ? (
        <section className="mt-8">
          <h2 className="h-font text-xl font-bold">Comments · {v.comments_count}</h2>
          <div className="flex gap-2 mt-4">
            <input value={text} onChange={e=>setText(e.target.value)} placeholder={user?"Add a comment…":"Sign in to comment"}
                   disabled={!user} data-testid="comment-input"
                   className="flex-1 h-11 rounded-full bg-white/5 border border-white/10 px-4 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#08D9D6]"/>
            <button onClick={comment} data-testid="comment-submit-btn" className="jx-btn h-11 w-11 rounded-full bg-[#08D9D6] text-black flex items-center justify-center hover:bg-[#06B2B0]"><Send size={16}/></button>
          </div>
          <div className="mt-6 space-y-4" data-testid="comments-list">
            {comments.map(c => (
              <div key={c.id} className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold shrink-0">
                  {c.username[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="text-xs text-white/60">@{c.username} · {new Date(c.created_at).toLocaleDateString()}</div>
                  <div className="text-sm mt-0.5">{c.text}</div>
                </div>
              </div>
            ))}
            {comments.length===0 && <div className="text-white/50 text-sm">Be the first to comment.</div>}
          </div>
        </section>
      ) : (
        <div className="mt-6 text-sm text-white/40">Comments are disabled by the creator.</div>
      )}

      {/* Tip dialog */}
      {showTip && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={()=>setShowTip(false)}>
          <div onClick={e=>e.stopPropagation()} className="bg-[#121216] border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="h-font text-xl font-bold">Tip @{v.creator?.username}</h3>
            <p className="text-xs text-white/60 mt-1">Your balance: <span className="text-[#FFD700] font-bold">{Math.floor(user?.coins||0)} coins</span></p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {TIP_PRESETS.map(c => (
                <button key={c} onClick={()=>sendTip(c)} data-testid={`tip-preset-${c}`}
                        className="jx-btn h-12 rounded-xl bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/40 font-bold hover:bg-[#FFD700]/25">
                  {c} coins
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <input type="number" value={tipCustom} onChange={e=>setTipCustom(e.target.value)} placeholder="Custom" data-testid="tip-custom"
                     className="flex-1 h-11 rounded-xl bg-white/5 border border-white/10 px-3 focus:outline-none focus:border-[#FFD700]"/>
              <button onClick={()=>sendTip(Number(tipCustom))} data-testid="tip-custom-send"
                      className="jx-btn h-11 px-4 rounded-xl bg-[#FFD700] text-black font-bold">Send</button>
            </div>
          </div>
        </div>
      )}

      {/* Cast dialog */}
      {showCast && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={()=>setShowCast(false)}>
          <div onClick={e=>e.stopPropagation()} className="bg-[#121216] border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="h-font text-xl font-bold flex items-center gap-2"><Cast size={18}/> Cast to a device</h3>
            {isCastSupported() ? (
              <div className="mt-4 space-y-2">
                <button onClick={castNow} data-testid="cast-chromecast" className="w-full text-left px-4 h-12 rounded-xl bg-[#08D9D6] text-black font-bold jx-btn inline-flex items-center gap-2">
                  <Tv size={16}/> {castInfo.connected ? 'Continue casting on ' + (castInfo.deviceName || 'TV') : 'Pick a Chromecast / Google TV'}
                </button>
                {castInfo.connected && (
                  <button onClick={castStop} data-testid="cast-stop" className="w-full text-left px-4 h-11 rounded-xl bg-[#FF2E63]/20 text-[#FF2E63] font-bold jx-btn">Stop casting</button>
                )}
              </div>
            ) : (
              <div className="mt-4 text-sm text-white/70">
                Cast isn't available in this browser. Use <span className="text-[#08D9D6] font-semibold">Google Chrome on desktop</span>, or open Jixxeo on your phone with Google Home installed to see nearby Chromecasts and Google TVs.
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-white/10 text-xs text-white/50 space-y-1">
              <div>• AirPlay: iOS/macOS users can tap the AirPlay icon in the player</div>
              <div>• Roku / Fire TV: dedicated Jixxeo apps are on the roadmap</div>
            </div>
          </div>
        </div>
      )}

      {/* Download quality picker */}
      {showDl && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={()=>!sdPrep && setShowDl(false)}>
          <div onClick={e=>e.stopPropagation()} className="bg-[#121216] border border-white/10 rounded-2xl p-6 w-full max-w-sm" data-testid="download-quality-dialog">
            <h3 className="h-font text-xl font-bold flex items-center gap-2"><Download size={18}/> Save for offline</h3>
            <div className="mt-4 space-y-2">
              <button onClick={()=>startDownload("sd")} disabled={sdPrep} data-testid="download-quality-sd"
                className="w-full text-left px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-[#FFD700]/30 jx-btn">
                <div className="font-bold text-sm text-[#FFD700]">{sdPrep ? "Preparing data saver…" : "Data saver (480p)"}</div>
                <div className="text-xs text-white/50">Smaller file, uses less storage & data</div>
              </button>
              <button onClick={()=>startDownload("full")} disabled={sdPrep} data-testid="download-quality-full"
                className="w-full text-left px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-[#00E7FF]/30 jx-btn">
                <div className="font-bold text-sm text-[#00E7FF]">Full quality</div>
                <div className="text-xs text-white/50">Original resolution, larger file</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Playlist picker */}
      {showPlaylist && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={()=>setShowPlaylist(false)}>
          <div onClick={e=>e.stopPropagation()} className="bg-[#121216] border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="h-font text-xl font-bold">Add to playlist</h3>
            <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
              {playlists.length === 0 && <div className="text-sm text-white/50">You have no playlists yet. Create one in Playlists.</div>}
              {playlists.map(p=>(
                <button key={p.id} onClick={()=>addToPlaylist(p.id)} className="w-full text-left px-4 h-11 rounded-xl bg-white/5 hover:bg-white/10 jx-btn">
                  {p.name} <span className="text-white/40 text-xs">· {p.count}</span>
                </button>
              ))}
            </div>
            <Link to="/playlists" onClick={()=>setShowPlaylist(false)} className="mt-3 inline-block text-sm text-[#08D9D6]">Manage playlists →</Link>
          </div>
        </div>
      )}
    </div>
  );
}
